import { useCurrentTeamMember } from '@/providers/TeamMemberProvider';
import { useCurrentPurchaseOrderContractTask } from '@/providers/PurchaseOrderContractTaskProvider';
import { usePurchaseOrderContractTaskComments, usePurchaseOrderContractTaskCommentsInvalidate } from '@/hooks/api/0930_purchase_order_contract_task_comments';
import { useEffect, useState } from 'react';
import { useCreatePurchaseOrderContractTaskComment } from '@/hooks/api/0940_create_purchase_order_contract_task_comment';
import { Comments as _Comments, OnCreateCommentProps, OnDeleteCommentProps, OnToggleReactionProps, OnUpdateCommentProps } from '@/components/element/Comments';
import { useParams } from 'react-router-dom';
import { usePurchaseOrderMembers } from '@/hooks/api/0590_purchase_order_members';
import { usePurchaseOrderContractTaskCommentReaction } from '@/hooks/api/0941_purchase_order_contract_task_comment_reactions';
import { MentionData } from '@draft-js-plugins/mention';
import { usePurchaseOrderTargetUsers } from '@/hooks/api/0541_purchase_order_target_users';
import { useUpdatePurchaseOrderComment } from '@/hooks/api/0632_update_purchase_order_comment';
import { useDeletePurchaseOrderContractTaskComment } from '@/hooks/api/0943_destroy_purchase_order_contract_task_comment';
import { useUpdatePurchaseOrderContractTaskComment } from '@/hooks/api/0942_update_purchase_order_contract_task_comment';

export const Comments = () => {
  const { _w_, _t_, me } = useCurrentTeamMember();
  const { purchase_order_id } = useParams();
  const { purchaseOrderContractTask: task } = useCurrentPurchaseOrderContractTask();
  const [mentions, setMentions] = useState<MentionData[]>([]);
  const { data: comments } = usePurchaseOrderContractTaskComments({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    purchase_order_id: Number(purchase_order_id),
    task_id: task!.id,
  });
  const { mutate, data, isPending, error } = useCreatePurchaseOrderContractTaskComment();
  const { mutate: updateMutate } = useUpdatePurchaseOrderContractTaskComment();
  const { mutate: deleteMutate } = useDeletePurchaseOrderContractTaskComment();
  const { invalidate: invalidateComments } = usePurchaseOrderContractTaskCommentsInvalidate({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    purchase_order_id: Number(purchase_order_id),
    task_id: task!.id,
  });
  const { mutate: reactionMutate } = usePurchaseOrderContractTaskCommentReaction();
  const { data: members } = usePurchaseOrderTargetUsers({ identifier: _w_!.identifier, team_id: _t_!.id, id: Number(purchase_order_id) });

  useEffect(() => {
    if (members) {
      setMentions(() =>
        members.map((d) => ({
          id: d.id,
          name: d.user_account.name,
          avatar: d.user_account.icon_file_url,
        }))
      );
    }
  }, [members]);

  const onCreateComment = ({ content, content_json, setComment, setError, parent_id }: OnCreateCommentProps) => {
    mutate(
      {
        identifier: _w_!.identifier,
        team_id: _t_!.id,
        purchase_order_id: Number(purchase_order_id),
        task_id: task!.id,
        content: content!,
        content_json: content_json,
        parent_id: parent_id,
      },
      {
        onSuccess: (res) => {
          setComment(res);
        },
        onError: (error: any) => {
          setError(error);
        },
        onSettled: () => {},
      }
    );
  };

  const onUpdateComment = ({ id, content, content_json, setComment, setError }: OnUpdateCommentProps) => {
    updateMutate(
      {
        identifier: _w_!.identifier,
        team_id: _t_!.id,
        purchase_order_id: Number(purchase_order_id),
        task_id: task!.id,
        id: id,
        content: content!,
        content_json: content_json,
      },
      {
        onSuccess: (res) => {
          setComment(res);
        },
        onError: (error: any) => {
          setError(error);
        },
        onSettled: () => {},
      }
    );
  };

  const onDeleteComment = ({ id, setComment, setError }: OnDeleteCommentProps) => {
    deleteMutate(
      {
        identifier: _w_!.identifier,
        team_id: _t_!.id,
        purchase_order_id: Number(purchase_order_id),
        task_id: task!.id,
        id: id,
      },
      {
        onSuccess: (res) => {
          setComment(res);
          invalidateComments();
        },
        onError: (error: any) => {
          setError(error);
        },
        onSettled: () => {},
      }
    );
  };

  const onToggleReaction = ({ comment_id, emoji_id, content, setComment, setError }: OnToggleReactionProps) => {
    reactionMutate(
      { identifier: _w_!.identifier, team_id: _t_!.id, purchase_order_id: Number(purchase_order_id), task_id: task!.id, id: comment_id, emoji_id, content },
      {
        onSuccess: (res) => {
          setComment(res);
        },
        onError: (error: any) => setError(error),
        onSettled: () => {},
      }
    );
  };

  if (mentions.length == 0) return <></>;

  return (
    <_Comments
      identifier={`purchase_order_contract_${_w_!.identifier}_${_t_!.id}_${purchase_order_id}_${task!.id}`}
      comments={comments}
      user={me!}
      mentions={mentions}
      onCreateComment={onCreateComment}
      onToggleReaction={onToggleReaction}
      onUpdateComment={onUpdateComment}
      onDeleteComment={onDeleteComment}
    />
  );
};
